/**
 * Standalone toast utility using a pub/sub pattern.
 *
 * Can be called from anywhere — utilities, hooks, components — without
 * requiring React context, Redux, or hooks at the call site.
 *
 * A `ToastListener` React component subscribes to these events and
 * bridges them to the sonner-backed `AppToaster` for rendering.
 *
 * @example
 * ```ts
 * import { toast } from '@/utilities/toast'
 *
 * toast.success('Layer deleted successfully')
 * toast.error('Failed to save changes')
 * toast.info('Upload in progress')
 * ```
 */

type ToastType = 'success' | 'error' | 'info'

interface ToastEvent {
  type: ToastType
  message: string
}

type ToastListener = (event: ToastEvent) => void

const listeners = new Set<ToastListener>()

const emit = (type: ToastType, message: string) => {
  if (!message) return
  listeners.forEach(listener => listener({ type, message }))
}

export const toast = {
  success: (message: string) => emit('success', message),
  error: (message: string) => emit('error', message),
  info: (message: string) => emit('info', message),

  /** Subscribe to toast events. Returns an unsubscribe function. */
  subscribe: (listener: ToastListener) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}
